
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import styles from './AppraisalReport.module.css';
import { generateAppraisalPDF } from '../../utils/generateAppraisalPDF';
import logo from '../../assets/logo.png';

const AppraisalReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pdfLoading, setPdfLoading] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [showPeriodManager, setShowPeriodManager] = useState(false);
  const [newPeriodForm, setNewPeriodForm] = useState({ startDate: '', endDate: '', deadlineDate: '' });
  const [deadlineEdits, setDeadlineEdits] = useState({}); // { [periodId]: 'yyyy-mm-dd' }
  const [periodSavingId, setPeriodSavingId] = useState(null);
  const [creatingPeriod, setCreatingPeriod] = useState(false);
  const [periodError, setPeriodError] = useState('');
  const [frozenSavingId, setFrozenSavingId] = useState(null);

  const isHR = user?.isHR;
  const isHOD = user?.isHOD;
  const isManager = user?.isManager;

  const formatPeriodLabel = (p) =>
    `${new Date(p.startDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} — ${new Date(p.endDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;

  const formatDeadline = (d) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const selectedPeriod = periods.find((p) => p.id === selectedPeriodId);
  const periodQuery = selectedPeriodId ? `reviewPeriodId=${selectedPeriodId}` : '';

  useEffect(() => {
    if (!isHR && !isHOD && !isManager) { navigate('/appraisal'); return; }
    fetchPeriods();
  }, [user]);

  useEffect(() => {
    setDeadlineEdits((prev) => {
      const next = { ...prev };
      periods.forEach((p) => { if (next[p.id] === undefined) next[p.id] = p.deadlineDate || ''; });
      return next;
    });
  }, [periods]);

  const fetchPeriods = async () => {
    try {
      const res = await api.getReviewPeriods();
      if (res.success && res.data?.length) {
        setPeriods(res.data);
        setSelectedPeriodId((prev) => prev ?? res.data[0].id);
      }
    } catch { }
  };

  // Guards against out-of-order responses when switching periods quickly — only the
  // most recently fired fetchForms() call is allowed to update state.
  const fetchRequestIdRef = useRef(0);

  const fetchForms = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    try {
      if (isHR) {
        const res = await api.getAllAppraisalForms(selectedPeriodId);
        if (requestId !== fetchRequestIdRef.current) return;
        if (res.success) setForms(res.data);
        return;
      }

      // A user can be HOD for some employees and reporting manager for others
      // at the same time, so both lists must be fetched and merged.
      const requests = [];
      if (isHOD) requests.push(api.getDepartmentAppraisalForms(selectedPeriodId));
      if (isManager) requests.push(api.getTeamAppraisalForms(selectedPeriodId));

      const results = await Promise.all(requests);
      if (requestId !== fetchRequestIdRef.current) return;

      const merged = new Map();
      results.forEach((res) => {
        if (res.success) res.data.forEach((f) => merged.set(f.employeeId, f));
      });
      setForms(Array.from(merged.values()));
    } catch { }
    finally { if (requestId === fetchRequestIdRef.current) setLoading(false); }
  }, [isHR, isHOD, isManager, selectedPeriodId]);

  useEffect(() => {
    if (selectedPeriodId) fetchForms();
  }, [selectedPeriodId, fetchForms]);

  const handleCreatePeriod = async () => {
    if (!newPeriodForm.startDate || !newPeriodForm.endDate || !newPeriodForm.deadlineDate) {
      setPeriodError('Start date, end date and deadline are all required.');
      return;
    }
    setCreatingPeriod(true);
    setPeriodError('');
    try {
      const res = await api.createReviewPeriod(newPeriodForm);
      if (res.success) {
        setPeriods((prev) => [res.data, ...prev].sort((a, b) => new Date(b.startDate) - new Date(a.startDate)));
        setSelectedPeriodId(res.data.id);
        setNewPeriodForm({ startDate: '', endDate: '', deadlineDate: '' });
      } else {
        setPeriodError(res.message || 'Failed to create period.');
      }
    } catch {
      setPeriodError('Failed to create period.');
    } finally {
      setCreatingPeriod(false);
    }
  };

  const handleUpdateDeadline = async (periodId) => {
    const newDeadline = deadlineEdits[periodId];
    if (!newDeadline) return;
    setPeriodSavingId(periodId);
    setPeriodError('');
    try {
      const res = await api.updateReviewPeriodDeadline(periodId, newDeadline);
      if (res.success) {
        setPeriods((prev) => prev.map((p) => (p.id === periodId ? res.data : p)));
      } else {
        setPeriodError(res.message || 'Failed to update deadline.');
      }
    } catch {
      setPeriodError('Failed to update deadline.');
    } finally {
      setPeriodSavingId(null);
    }
  };

  const handleToggleFrozen = async (periodId, currentlyFrozen) => {
    setFrozenSavingId(periodId);
    setPeriodError('');
    try {
      const res = await api.setReviewPeriodFrozen(periodId, !currentlyFrozen);
      if (res.success) {
        setPeriods((prev) => prev.map((p) => (p.id === periodId ? res.data : p)));
      } else {
        setPeriodError(res.message || 'Failed to update freeze status.');
      }
    } catch {
      setPeriodError('Failed to update freeze status.');
    } finally {
      setFrozenSavingId(null);
    }
  };

  const filtered = forms.filter((f) =>
    !search ||
    f.employeeName?.toLowerCase().includes(search.toLowerCase()) ||
    f.reportingManager?.toLowerCase().includes(search.toLowerCase())
  );

  const totalEmp      = forms.length;
  const empFilled     = forms.filter((f) => f.employeeSubmitted).length;
  const managerFilled = forms.filter((f) => f.managerSubmitted).length;
  const hodFilled     = forms.filter((f) => f.hodSubmitted).length;
  const allFilled     = forms.filter((f) => f.employeeSubmitted && f.managerSubmitted && f.hodSubmitted).length;

  const handleCompleteNow = (employeeId) => navigate(`/appraisal/${employeeId}${periodQuery ? `?${periodQuery}` : ''}`);
  const handleReview      = (employeeId) => navigate(`/appraisal/${employeeId}?review=true${periodQuery ? `&${periodQuery}` : ''}`);

  // ===== PDF DOWNLOAD =====
  const handleDownloadPDF = async (employeeId) => {
    setPdfLoading(employeeId);
    try {
      const res = await api.getEmployeeAppraisalForm(employeeId, selectedPeriodId);
      if (res.success && res.data) {
        const response = await fetch(logo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = () => {
          generateAppraisalPDF(res.data, reader.result);
          setPdfLoading(null);
        };
        reader.onerror = () => {
          generateAppraisalPDF(res.data, null);
          setPdfLoading(null);
        };
        reader.readAsDataURL(blob);
      }
    } catch (e) {
      console.error('PDF error:', e);
      setPdfLoading(null);
    }
  };

  const getRatingClass = (r) => {
    if (!r) return '';
    if (r.includes('RI')) return styles.ratingRI;
    if (r.includes('AS')) return styles.ratingAS;
    return styles.ratingCS;
  };

  const StatusPill = ({ filled }) => (
    <span className={filled ? styles.statusFilled : styles.statusPending}>
      {filled ? '✅ Done' : '⏳ Pending'}
    </span>
  );

  const renderAction = (f) => {
    const allThreeDone = f.employeeSubmitted && f.managerSubmitted && f.hodSubmitted;
    const frozen = !!selectedPeriod?.isFrozen;

    const viewerFirstName   = user?.name?.split(' ')[0]?.toLowerCase();
    const reportingMgrLower = f.reportingManager?.toLowerCase() || '';
    const reportsToViewer   =
      reportingMgrLower === viewerFirstName ||
      viewerFirstName?.includes(reportingMgrLower) ||
      reportingMgrLower?.includes(viewerFirstName);

    // Reporting manager can open an employee's form in any state (draft, not started, or
    // submitted) for the latest review period — for older periods it stays gated behind
    // the employee having submitted, same as before. On the latest period the manager can
    // also come back and re-submit after their own submission (see canEditManager).
    const managerAction = () => {
      if (f.managerSubmitted) {
        return selectedPeriod?.isLatest ? (
          <button className={styles.completeBtn}
            onClick={() => handleCompleteNow(f.employeeId)}>
            Manager: Edit Submission
          </button>
        ) : null;
      }
      if (f.employeeSubmitted || selectedPeriod?.isLatest) {
        return (
          <button className={styles.completeBtn}
            onClick={() => handleCompleteNow(f.employeeId)}>
            Manager: Complete Now
          </button>
        );
      }
      return <span className={styles.waitingBadge}>⏳ Awaiting Employee</span>;
    };

    const hodAction = () => {
      if (f.hodSubmitted) return null;
      // Viewer is this employee's reporting manager (not just their HOD) — same relaxed
      // rule as managerAction, needed since only one of managerAction/hodAction ever runs
      // for a viewer who holds both roles.
      if (reportsToViewer && !f.managerSubmitted && (f.employeeSubmitted || selectedPeriod?.isLatest)) {
        return (
          <button className={styles.completeBtn}
            onClick={() => handleCompleteNow(f.employeeId)}>
            Manager: Complete Now
          </button>
        );
      }
      if (f.managerSubmitted) {
        return (
          <button className={styles.completeBtn}
            style={{ background: 'linear-gradient(135deg, #be185d, #db2777)' }}
            onClick={() => handleCompleteNow(f.employeeId)}>
            HOD: Complete Now
          </button>
        );
      }
      return <span className={styles.waitingBadge}>⏳ Awaiting Manager</span>;
    };

    // const reviewBtn = () => {
    //   if ((isHR || isHOD) && allThreeDone) {
    //     return (
    //       <button className={styles.reviewBtn}
    //         onClick={() => handleReview(f.employeeId)}>
    //         👁 Review
    //       </button>
    //     );
    //   }
    //   if (isManager && !isHOD && f.managerSubmitted) {
    //     return (
    //       <button className={styles.reviewBtn}
    //         onClick={() => handleReview(f.employeeId)}>
    //         👁 Review
    //       </button>
    //     );
    //   }
    //   return null;
    // };

    const reviewBtn = () => {
      if (!f.employeeSubmitted) return null;

      return (
        <button className={styles.reviewBtn}
          onClick={() => handleReview(f.employeeId)}>
          👁 Review
        </button>
      );
    };

    // if (isHR && !isHOD && !isManager) {
    //   return allThreeDone ? (
    //     <button className={styles.reviewBtn} onClick={() => handleReview(f.employeeId)}>
    //       👁 Review
    //     </button>
    //   ) : <span className={styles.readOnly}>—</span>;
    // }

    // if (isHR && !isHOD && !isManager) {
    //   return f.employeeSubmitted ? (
    //     <button className={styles.reviewBtn} onClick={() => handleReview(f.employeeId)}>
    //       👁 Review
    //     </button>
    //   ) : <span className={styles.readOnly}>—</span>;
    // }

    if (isHR && !isHOD && !isManager) {
  return (
    <div className={styles.actionGroup}>
      {frozen ? (
        <span className={styles.waitingBadge}>🔒 Frozen</span>
      ) : (
        <button className={styles.editHrBtn}
          onClick={() => navigate(`/appraisal/${f.employeeId}${periodQuery ? `?${periodQuery}` : ''}`)}>
          ✏️ Edit
        </button>
      )}
      {f.employeeSubmitted && (
        <button className={styles.reviewBtn}
          onClick={() => handleReview(f.employeeId)}>
          👁 Review
        </button>
      )}
    </div>
  );
}

    if (isHOD) {
      return (
        <div className={styles.actionGroup}>
          {frozen ? <span className={styles.waitingBadge}>🔒 Frozen</span> : hodAction()}
          {reviewBtn()}
        </div>
      );
    }

    if (isManager) {
      return (
        <div className={styles.actionGroup}>
          {frozen ? <span className={styles.waitingBadge}>🔒 Frozen</span> : managerAction()}
          {reviewBtn()}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.container}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Appraisal Report</h1>
          <p className={styles.subtitle}>
            {isHR && !isHOD ? 'Overview of all employee appraisal submissions' :
             isHOD ? 'Review and complete appraisals for all employees' :
             'Review and complete appraisals for your team'}
          </p>
        </div>
        <div className={styles.headerRight}>
          {/* Review Period */}
          <div className={styles.reviewPeriodBox}>
            <span className={styles.rpLabel}>Review Period</span>
            <div className={styles.rpValue}>
              {periods.length > 0 ? (
                <select
                  className={styles.rpSelect}
                  value={selectedPeriodId || ''}
                  onChange={(e) => setSelectedPeriodId(Number(e.target.value))}>
                  {periods.map((p) => (
                    <option key={p.id} value={p.id}>{formatPeriodLabel(p)}</option>
                  ))}
                </select>
              ) : (
                <span>Loading...</span>
              )}
              {isHR && (
                <button className={styles.rpEdit2} onClick={() => setShowPeriodManager((s) => !s)}>
                  ⚙️ Manage
                </button>
              )}
            </div>
            {selectedPeriod && (
              <div className={styles.rpDeadlineHint}>
                Deadline: <strong>{formatDeadline(selectedPeriod.deadlineDate)}</strong>
              </div>
            )}
            {selectedPeriod?.isFrozen && (
              <div className={styles.frozenBanner}>
                🔒 This review period is frozen — no one (employee, manager, HOD, or HR) can edit or submit forms for it.
              </div>
            )}

            {isHR && showPeriodManager && (
              <div className={styles.periodPanel}>
                {periodError && <div className={styles.periodPanelError}>{periodError}</div>}

                <div className={styles.periodPanelSection}>
                  <span className={styles.periodPanelTitle}>All review periods &amp; their deadlines</span>
                  <div className={styles.periodList}>
                    {periods.map((p) => (
                      <div key={p.id} className={styles.periodListRow}>
                        <span className={styles.periodListLabel}>
                          {formatPeriodLabel(p)}
                          {p.isLatest && <span className={styles.periodLatestTag}>Latest</span>}
                          {p.isFrozen && <span className={styles.periodFrozenTag}>🔒 Frozen</span>}
                        </span>
                        <span className={styles.periodListDeadlineLabel}>Deadline</span>
                        <input type="date" className={styles.rpInput}
                          value={deadlineEdits[p.id] ?? ''}
                          onChange={(e) => setDeadlineEdits((d) => ({ ...d, [p.id]: e.target.value }))} />
                        <button className={styles.rpSave}
                          disabled={periodSavingId === p.id}
                          onClick={() => handleUpdateDeadline(p.id)}>
                          {periodSavingId === p.id ? 'Saving...' : 'Save'}
                        </button>
                        <button className={p.isFrozen ? styles.rpUnfreeze : styles.rpFreeze}
                          disabled={frozenSavingId === p.id}
                          onClick={() => handleToggleFrozen(p.id, p.isFrozen)}>
                          {frozenSavingId === p.id ? 'Saving...' : p.isFrozen ? '🔓 Unfreeze' : '🔒 Freeze'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.periodPanelSection}>
                  <span className={styles.periodPanelTitle}>Create new review period</span>
                  <div className={styles.periodCreateGrid}>
                    <label className={styles.periodFieldLabel}>
                      Start date
                      <input type="date" className={styles.rpInput}
                        value={newPeriodForm.startDate}
                        onChange={(e) => setNewPeriodForm((p) => ({ ...p, startDate: e.target.value }))} />
                    </label>
                    <label className={styles.periodFieldLabel}>
                      End date
                      <input type="date" className={styles.rpInput}
                        value={newPeriodForm.endDate}
                        onChange={(e) => setNewPeriodForm((p) => ({ ...p, endDate: e.target.value }))} />
                    </label>
                    <label className={styles.periodFieldLabel}>
                      Deadline date
                      <input type="date" className={styles.rpInput}
                        value={newPeriodForm.deadlineDate}
                        onChange={(e) => setNewPeriodForm((p) => ({ ...p, deadlineDate: e.target.value }))} />
                    </label>
                    <button className={styles.rpSave} disabled={creatingPeriod} onClick={handleCreatePeriod}>
                      {creatingPeriod ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className={styles.searchBar}>
            <span>🔍</span>
            <input className={styles.searchInput}
              placeholder="Search by name or manager..."
              value={search}
              onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--color-primary)' }}>{totalEmp}</div>
          <div className={styles.statLabel}>Total</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--color-green)' }}>{empFilled}</div>
          <div className={styles.statLabel}>Employee Filled</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--color-secondary)' }}>{managerFilled}</div>
          <div className={styles.statLabel}>Manager Filled</div>
        </div>
        {(isHR || isHOD) && (
          <div className={styles.statCard}>
            <div className={styles.statValue} style={{ color: '#d97706' }}>{hodFilled}</div>
            <div className={styles.statLabel}>HOD Filled</div>
          </div>
        )}
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--color-green-dark)' }}>{allFilled}</div>
          <div className={styles.statLabel}>All Complete</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue} style={{ color: '#e53e3e' }}>{totalEmp - empFilled}</div>
          <div className={styles.statLabel}>Pending</div>
        </div>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <div className={styles.tableScroll}>
          {loading ? (
            <div className={styles.loading}>Loading report...</div>
          ) : filtered.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📋</div>
              <p>No records found</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Reporting Manager</th>
                  <th>Employee</th>
                  <th>Manager</th>
                  {(isHR || isHOD) && <th>HOD</th>}
                  <th>Score</th>
                  <th>Rating</th>
                  <th>Action</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f, idx) => (
                  <tr key={f.employeeId}>
                    <td style={{ color: 'var(--color-text-light)', fontSize: 12 }}>{idx + 1}</td>
                    <td><strong>{f.employeeName}</strong></td>
                    <td>{f.reportingManager || '—'}</td>
                    <td><StatusPill filled={f.employeeSubmitted} /></td>
                    <td><StatusPill filled={f.managerSubmitted} /></td>
                    {(isHR || isHOD) && <td><StatusPill filled={f.hodSubmitted} /></td>}
                    <td>
                      {f.overallScore && Number(f.overallScore) > 0
                        ? <strong style={{ color: 'var(--color-secondary)' }}>{Number(f.overallScore).toFixed(1)}</strong>
                        : <span style={{ color: 'var(--color-text-light)' }}>—</span>
                      }
                    </td>
                    <td>
                      {f.overallRating ? (
                        <span className={`${styles.ratingBadge} ${getRatingClass(f.overallRating)}`}>
                          {f.overallRating}
                        </span>
                      ) : <span style={{ color: 'var(--color-text-light)' }}>—</span>}
                    </td>
                    <td>{renderAction(f)}</td>
                    <td>
                      {f.employeeSubmitted ? (
                        <button
                          className={styles.pdfBtn}
                          onClick={() => handleDownloadPDF(f.employeeId)}
                          disabled={pdfLoading === f.employeeId}
                          title="Download PDF">
                          {pdfLoading === f.employeeId ? '⏳' : '📄 PDF'}
                        </button>
                      ) : (
                        <span style={{ color: 'var(--color-text-light)', fontSize: 11 }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!loading && (
          <div className={styles.tableFooter}>
            Showing {filtered.length} of {totalEmp} employees
          </div>
        )}
      </div>
    </div>
  );
};

export default AppraisalReport;